import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useCart from '../../providers/cart';
import useCompose from '../../hooks/useCompose';
import useToggle from '../../hooks/useToggle';
import Container from '../../styled/Container';
import Main from '../../styled/Header/Main';
import { Item, Link, List, QuickNav } from '../../styled/Header/QuickNav/wrappers';
import Ribbon from '../../styled/Header/Ribbon';
import { LogInIcon, PhoneIcon, StoreIcon, UserIcon } from '../../styled/Icon';
import Navigation from './Navigation';
import Search from './Search';

import RouterLink from '../../Router/Link';
import Distance from '../../styled/Distance';
import { useIsAuthorized } from '../../providers/member';
import useContext from '../../hooks/useContext';
import { StoreContext } from '../../providers/store';
import { useTrackGenericEvent } from '../../hooks/tracking/useTrackingEvents';

type RibbonIcon = 'icon-headset' | 'icon-user' | 'icon-store';

const resolveIcon = (icon: RibbonIcon) => {
  switch (icon) {
    case 'icon-headset':
      return <PhoneIcon />;
    case 'icon-store':
      return <StoreIcon />;
    case 'icon-user':
      return <UserIcon />;
    default:
      return null;
  }
};

interface IAction {
  className?: string;
  icon: JSX.Element | null;
  reload?: boolean;
  title: string;
  url: string;
  type: string;
  action?: () => void;
}

const storesAction = (action: () => void, store: ILimitedStore | undefined): IAction => ({
  type: 'action',
  icon: <StoreIcon />,
  title: store ? `${store?.storeName}` : 'Välj din butik',
  url: '',
  action: action,
});

const myPagesLink = (loggedIn: boolean): IAction => ({
  type: 'link',
  icon: loggedIn ? <UserIcon /> : <LogInIcon />,
  reload: true,
  title: loggedIn ? 'Mina sidor' : 'Logga in',
  url: '/mina-sidor',
});

const mapLink = (link: ILink): IAction => ({
  type: 'link',
  className: 'hidden-lt-md',
  icon: resolveIcon(link.icon as RibbonIcon),
  title: link.title,
  url: link.path,
});

interface ILinkItem {
  onSelect: (item: IMenuItem) => void;
  item: IMenuItem;
}

const LinkItem = ({ onSelect, item }: ILinkItem) => {
  const clickEvent = useMemo(
    () => ({
      event: 'top_navigation_click',
      value: item.name,
    }),
    [item.name],
  );

  const trackClick = useTrackGenericEvent(clickEvent);

  const click =
    useCallback(() => {
      if (item.openMenu) {
        onSelect(item);
      }
      trackClick();
    }, [onSelect, item]) || undefined;

  return (
    <Item>
      <RouterLink
        Component={Link}
        href={item.url}
        title={item.name}
        onClick={item.openMenu ? click : undefined}
        onBeforeNavigate={!item.openMenu ? click : undefined}>
        {item.name}
      </RouterLink>
    </Item>
  );
};

interface IHeader {
  links: ILink[];
  navigation: IMenuItem[];
  usps: string[];
}

const Header = ({ links, navigation, usps }: IHeader) => {
  const isAuthorized = useIsAuthorized();
  const { toggleShowUserStorePanel, userStore } = useContext(StoreContext);
  const [loggedIn, setLoggedIn] = useState(false);
  const [showMenu, toggleShowMenu] = useToggle(false);
  const [item, setItem] = useState<IMenuItem | undefined>();
  const { quantity } = useCart();

  const actionList = useMemo(
    () =>
      [storesAction(toggleShowUserStorePanel, userStore), myPagesLink(loggedIn)].concat(
        links?.map(mapLink) || [],
      ),
    [loggedIn, userStore],
  );

  useEffect(() => {
    setLoggedIn(isAuthorized);
  }, [isAuthorized]);

  const clickShortcut = useCallback((item: IMenuItem) => {
    setItem(item);
    toggleShowMenu();
  }, []);

  const close = useCompose(
    useCallback(() => setItem(undefined), []),
    toggleShowMenu,
  );

  return (
    <>
      <>
        <Ribbon uspList={usps} actionList={actionList} />
        <Distance size="1" />
        <Main
          cartCount={quantity}
          onMenuClick={toggleShowMenu}
          renderSearch={(isHidden: boolean) => <Search {...{ isHidden }} />}
        />
        <QuickNav>
          <Container>
            <List>
              {navigation?.map(
                (item) =>
                  !item.hiddenFromMenu && (
                    <LinkItem key={item.url} item={item} onSelect={clickShortcut} />
                  ),
              )}
            </List>
          </Container>
          <Distance size="2" />
        </QuickNav>
      </>
      {showMenu && <Navigation onClose={close} items={navigation} initialItem={item} />}
    </>
  );
};

export default Header;
